import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { FunctionComponent, useEffect, useState } from 'react'
import moment from 'moment'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import { IGroup } from '../../../../shared/services/group/group.model'
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service'
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons'
import { INominalOutcomeValue, NominalMappingOutcomePicker } from './nominal-mapping-outcome-picker'
import { INominalActivityOption } from './nominal-outcome-autocomplete'

/**
 * NominalOutcomeDrawer
 * -------------------------------------------------------------------------
 * The Advanced view of one code's outcome: the full NominalMappingOutcomePicker (source, activity,
 * group, reason) in a right-hand drawer, for the few codes that need more than the row's single
 * list offers. The picker is used unchanged. Apply writes back through the same change handler
 * the row uses; Cancel leaves the row as it was. Shell copied from the analytics DrillDownDrawer.
 */
export const NominalOutcomeDrawer: FunctionComponent<{
  open: boolean,
  nominalCode: string,
  accountName?: string | null,
  // The row's current outcome: its unsaved change, else its saved mapping, else null.
  value: INominalOutcomeValue | null,
  // Where an untouched row starts: the organisation's default spend source.
  defaultSourceId: string | null,
  // The default source's activities, already loaded by the page, so applying one of them needs no
  // second request.
  activities: INominalActivityOption[],
  sources: IConversionSource[],
  groups: IGroup[],
  onApply: (next: INominalOutcomeValue, conversion: ISpendBasedConversion | null) => void,
  onClose: () => void,
}> = ({ open, nominalCode, accountName, value, defaultSourceId, activities, sources, groups, onApply, onClose }) => {
  //Services
  const spendBasedConversionService = new SpendBasedConversionService()
  //State
  const [draft, setDraft] = useState<INominalOutcomeValue | null>(null)
  const [isApplying, setIsApplying] = useState<boolean>(false)

  //Seed the draft each time the drawer opens, so Cancel then reopen starts from the row again.
  useEffect(() => {
    if (!open) return
    setDraft(value ?? {
      mappingType: 'spend',
      spendBasedConversionId: null,
      conversionSourceId: defaultSourceId,
      excludeReason: null,
      groupId: null,
    })
    setIsApplying(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nominalCode])

  const isIncomplete = !draft || (draft.mappingType === 'spend' && !draft.spendBasedConversionId)

  //The row needs the activity itself (not only its id) to show an activity from another source,
  //so look it up: from the page's list when it is there, else from the source it was picked in.
  const applyHandler = async () => {
    if (!draft || isIncomplete || isApplying) return
    if (draft.mappingType !== 'spend' || !draft.spendBasedConversionId) {
      onApply(draft, null)
      return
    }
    const listed = activities.find(option => option.conversion.id === draft.spendBasedConversionId)
    if (listed || !draft.conversionSourceId) {
      onApply(draft, listed?.conversion ?? null)
      return
    }
    setIsApplying(true)
    try {
      const response: any = await spendBasedConversionService.getSpendBasedConversionsForConversionSource(draft.conversionSourceId, moment().year().toString(), true, true)
      const rows: ISpendBasedConversion[] = Array.isArray(response) ? response : (response?.rows || [])
      onApply(draft, rows.find(conversion => conversion.id === draft.spendBasedConversionId) ?? null)
    } catch (err) {
      console.log(err)
      // The outcome itself is complete; only its display name could not be fetched.
      onApply(draft, null)
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          top: { xs: 0, sm: 64 },
          height: { xs: '100%', sm: 'calc(100vh - 64px)' },
          borderTopLeftRadius: { xs: 0, sm: 24 },
          borderBottomLeftRadius: { xs: 0, sm: 24 },
          overflow: 'hidden',
          boxShadow: '-12px 0 32px rgba(17, 30, 33, 0.14)',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        {/* Header */}
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ px: 3, py: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant='caption' color='text.secondary'>Code {nominalCode}</Typography>
            <Typography variant='h6' sx={{ color: 'text.primary', lineHeight: 1.2, wordBreak: 'break-word' }}>
              {accountName || 'Unnamed account'}
            </Typography>
          </Box>
          <IconButton aria-label='Close' onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
            Choose a factor source other than your default, or place the code in a group to set its scope,
            for example a capital goods group for Scope 3 Category 2.
          </Typography>
          {draft && (
            <NominalMappingOutcomePicker
              value={draft}
              onChange={setDraft}
              sources={sources}
              groups={groups}
            />
          )}
        </Box>

        <Stack direction='row' justifyContent='flex-end' alignItems='center' spacing={1} sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <DialogSaveButtons
            cancelHandler={onClose}
            submitHandler={applyHandler}
            isSubmitting={isApplying}
            buttonInitialText='Apply'
            buttonLoadingText='Applying'
            disableSaveButton={isIncomplete}
            disabledReason='Choose a spend activity'
          />
        </Stack>
      </Box>
    </Drawer>
  )
}
